import * as React from "react";
import { Controller, FieldValues, Path, useFormContext } from "react-hook-form";
import { useLanguage } from "../../Language";
import {
  Autocomplete,
  AutocompleteProps,
  TextField,
} from "@mui/material";
import { SMSGateway } from "../../OBRSettings";

export interface MobileCarrierProps<T extends FieldValues>
  extends Pick<
    AutocompleteProps<number, false, false, false>,
    "sx" | "disabled"
  > {
  name: Path<T>;
  required: boolean;
  smsGateways: SMSGateway[];
}

export const MobileCarrier = <T extends FieldValues>({
  name,
  required,
  disabled,
  smsGateways,
  sx,
}: MobileCarrierProps<T>) => {
  const { control, getFieldState, formState } = useFormContext<T>();

  const { translate } = useLanguage();

  const carrierNameMap = React.useMemo(() => {
    const carrierNameMap = new Map();
    (smsGateways || []).forEach((gateway) =>
      carrierNameMap.set(gateway.id, gateway.carrierName)
    );
    return carrierNameMap;
  }, [smsGateways]);

  const { error } = getFieldState(name, formState);

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        required: required ? translate("mobileCarrierRequired") : false,
      }}
      render={({ field: { ref, onChange, ...field } }) => (
        <Autocomplete
          disabled={disabled}
          sx={{ maxWidth: { sm: "320px" }, ...sx }}
          {...field}
          onChange={(event, value) => onChange(value)}
          renderInput={(params) => (
            <TextField
              {...params}
              required={required}
              error={!!error}
              helperText={error ? <>{error.message}</> : " "}
              label={translate("ci_mobilePhoneSelectCarrier")}
              inputRef={ref}
            />
          )}
          options={(smsGateways || []).map((gateway) => gateway.id)}
          getOptionLabel={(option) => carrierNameMap.get(option) || `${option}`}
        ></Autocomplete>
      )}
    />
  );
};
